// src/modules/leaves/pages/LeavePage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import PageHeader from "components/Common/PageHeader";
import { all_routes } from "router/all_routes";
import CustomButton from "components/Common/ui/CustomButton";
import TableFilterDynamique, { FilterField } from "components/Common/TableFilter/TableFilterDynamique";
import useLeave from "../hooks/useLeave";
import useReference from "hooks/useReference";
import AddEditLeave from "../components/AddEditLeave";
import MyLeaveTable from "../components/MyLeaveTable";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import CustomAlert from "components/Common/ui/CustomAlert";



const MyLeavePage: React.FC = () => {
  const { t } = useTranslation();
  const { leaves, loading, fetchMyLeaves, infoPagination, setIsModalOpen, isModalOpen, handleCreate, handleUpdate, statusLeaveOptions, handleDelete } = useLeave();
  const { leaveTypes, loadingLeaveTypes, fetchLeaveTypes } = useReference();
  const user = useSelector((state: RootState) => state.authSlice.user);
  const [filters, setFilters] = useState<{ [key: string]: string | number | null }>({});
  const [selectedLeave, setSelectedLeave] = useState<any>(null);
  const breadcrumbItems = [
    { label: "dashboard", path: all_routes.dashboard },
    { label: "leave.title", active: true }, // add "leave.title" in i18n
  ];

  const leaveTypeOptions = useMemo(
    () => (leaveTypes || []).map((lt) => ({ label: lt.name, value: lt.id })),
    [leaveTypes]
  );


  const filterFields: FilterField[] = useMemo(
    () => [
      // { key: "search", label: t("search"), type: "text" },
      { key: "status", label: t("leave.form.status"), type: "select", options: statusLeaveOptions },
      { key: "leaveTypeId", label: t("leave.form.leaveType"), type: "select", options: leaveTypeOptions },
      // { key: "dateRange", label: t("date_range"), type: "range" },
    ],
    [t, statusLeaveOptions, leaveTypeOptions, loadingLeaveTypes]
  );

  const hasStaff = useMemo(() => {
    return user?.hasStaff;
  }, [user]);

  const handleSave = async (payload: any) => {
    try {
      let result = false;
      if (selectedLeave?.id) {
        result = await handleUpdate(selectedLeave?.id, payload);
      } else {
        result = await handleCreate(payload);
      }

      // close modal if success
      if (result) {
        setIsModalOpen(false);
      }
    } catch (error) {
      console.error("Error fetching leaves:", error);
    }

  };


  const handleFilter = (newFilters: { [key: string]: any }) => {
    const { dateRange, ...rest } = newFilters || {};
    const dateFrom = Array.isArray(dateRange) && dateRange[0] ? dateRange[0] : null;
    const dateTo = Array.isArray(dateRange) && dateRange[1] ? dateRange[1] : null;

    const merged = {
      ...rest,
      dateFrom,
      dateTo,
    };

    setFilters(merged);
    fetchMyLeaves({
      ...merged,
      page: 1,
      limit: 10,
    });
  };

  useEffect(() => {
    if (!hasStaff) {
      return;
    }
    fetchLeaveTypes();
    fetchMyLeaves({
      page: 1,
      limit: 10,
    });
  }, []);


  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader title={t("leave.title")} breadcrumbItems={breadcrumbItems} />

        {hasStaff ? (
          <>
            <div className="d-flex mb-3 justify-content-end gap-2">
              <CustomButton
                onClick={() => setIsModalOpen(true)}
              >
                <i className="ti ti-plus me-2" />
                {t("leave.add")}
              </CustomButton>
              <TableFilterDynamique
                fields={filterFields}
                filters={filters}
                onApply={handleFilter}
                onReset={() => handleFilter({ page: 1, limit: 10 })}
              />
            </div>

            <MyLeaveTable
              dataSource={leaves}
              loading={loading}
              onEdit={(record: any) => {
                setSelectedLeave(record);
                setIsModalOpen(true);
              }}
              onDelete={handleDelete}
              dataPaginate={{
                current: infoPagination?.page,
                pageSize: infoPagination?.limit,
                total: infoPagination?.total,
              }}
              onChange={(pg) => {
                fetchMyLeaves({
                  ...filters,
                  page: pg.current!,
                  limit: pg.pageSize!,
                });
              }}
            />

            <AddEditLeave
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              leaveData={selectedLeave}
              onSave={handleSave}
            />
          </>
        ) : (
          <CustomAlert
            message={t("alert.no_staff_alert.message")}
            description={t("alert.no_staff_alert.description")}
            type="info"
            showIcon
          />
        )}
      </div>
    </div>
  );
};

export default MyLeavePage;
